import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const ProfilingDialog = ({ open, onClose, profileData, sourceName, totalSampleRows = 100 }) => {

  if (!profileData) return null;

  const formatValue = (value) => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    return String(value);
  };

  // Group columns by data type
  const dataTypeGroups = {};
  profileData.columns?.forEach(col => {
    const type = col.data_type?.toLowerCase() || 'unknown';
    if (!dataTypeGroups[type]) {
      dataTypeGroups[type] = 0;
    }
    dataTypeGroups[type]++;
  });

  // Convert to array and sort by count
  const typeStats = Object.entries(dataTypeGroups)
    .map(([type, count]) => ({ type, count }))
    .sort((a, b) => b.count - a.count);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '60vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          backgroundImage: 'linear-gradient(to right, #4880EC, #019CAD)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Data Profiling: {sourceName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: 18, opacity: 0.9 }} />
            <Typography variant="caption" sx={{ fontSize: 18, opacity: 0.9 }}>
              Profiling based on {totalSampleRows} sample rows (not full dataset)
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2, p: 3, bgcolor: '#fafafa' }}>
        {/* Summary Stats - Minimal Design */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: '#374151' }}>
            Column Type Distribution
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {typeStats.map(({ type, count }) => (
              <Paper
                key={type}
                elevation={0}
                sx={{
                  px: 2,
                  py: 1.5,
                  bgcolor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 2,
                  minWidth: 120
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                  {type}
                </Typography>
                <Typography variant="h5" fontWeight={700} color="#1f2937">
                  {count}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Box>

        {/* Column Details Table - Clean Design */}
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: '#374151' }}>
          Column Details ({profileData.columns?.length || 0} columns)
        </Typography>
        <TableContainer 
          component={Paper} 
          elevation={0}
          sx={{ 
            border: '1px solid #e5e7eb',
            borderRadius: 2
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f9fafb' }}>
                <TableCell sx={{ fontWeight: 700, color: '#374151', borderBottom: '2px solid #e5e7eb' }}>
                  Column Name
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151', borderBottom: '2px solid #e5e7eb' }}>
                  Data Type
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151', borderBottom: '2px solid #e5e7eb' }} align="right">
                  Null Count
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151', borderBottom: '2px solid #e5e7eb' }} align="right">
                  Distinct Values
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {profileData.columns?.map((col, idx) => {
                const hasMinMax = (col.min !== null && col.min !== undefined) || 
                                  (col.max !== null && col.max !== undefined);

                return (
                  <TableRow
                    key={idx}
                    sx={{
                      '&:hover': { bgcolor: '#f9fafb' },
                      '&:last-child td': { borderBottom: 0 }
                    }}
                  >
                    <TableCell sx={{ maxWidth: 250 }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        fontFamily="monospace"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: '#1f2937'
                        }}
                        title={col.column_name}
                      >
                        {col.column_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={col.data_type}
                        size="small"
                        sx={{
                          fontSize: '0.7rem',
                          height: 22,
                          bgcolor: '#f3f4f6',
                          color: '#4b5563',
                          fontFamily: 'monospace',
                          fontWeight: 500,
                          border: '1px solid #e5e7eb'
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          color: col.null_count > 0 ? '#dc2626' : '#16a34a',
                          fontWeight: 600,
                          fontFamily: 'monospace'
                        }}
                      >
                        {formatValue(col.null_count)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        fontWeight={500}
                        fontFamily="monospace"
                        sx={{ color: '#4b5563' }}
                      >
                        {formatValue(col.distinct_count)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default ProfilingDialog;